import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { string, object } from 'yup';
// form
import { useForm, Controller } from 'react-hook-form';
// @mui
import {
  Card,
  Stack,
  Divider,
  Popover,
  Checkbox,
  MenuItem,
  IconButton,
  CardHeader,
  FormControlLabel,
  TextField,
  Box
} from '@mui/material';
import { useNotes } from '../../../hooks/Services/useNotes';
// components
import Iconify from '../../../components/iconify';

// ----------------------------------------------------------------------

AppTasks.propTypes = {
  title: PropTypes.string,
  subheader: PropTypes.string,
  list: PropTypes.array.isRequired,
};

export default function AppTasks({ title, subheader, list, ...other }) {
  const { fetchNote, saveNote, notes, setNotes, doneNote, deleteNote } = useNotes()
  const [valueNote, setValueNote] = useState('')
  const [yupError, setYupError] = useState(false);
  const { control } = useForm({
    defaultValues: {
      taskCompleted: ['1'],
    },
  });

  const schema = object().shape({
      id: string().min(3, 'Minimo de 3 caracteres'),
      note: string(),
  });
  
  const handleEnter = (key) => {
    if (key.keyCode === 13) {
      const newNote = { id: valueNote.trim(), note: valueNote };

      schema.validate(newNote, { abortEarly: false })
        .then((valid) => {
          saveNote({ ...newNote });
          setValueNote('');
          setYupError(false);
        })
        .catch(error => setYupError(true))
    }
  }

  const handleDelete = (note) => {
    deleteNote(note);
    setNotes((prevNotas) => prevNotas.filter((n) => n.id !== note.id));
  };

  useEffect(() => {
    fetchNote()
  }, [])

  return (
    <Card {...other}>
      <CardHeader title={title} subheader={subheader} />
      <Box width="85%" margin='1.5rem'>
        <TextField
          id="standard-basic"
          error={yupError}
          helperText={yupError && 'Mínimo de 3 caracteres'}
          label="Nota..."
          variant="standard"
          fullWidth
          value={valueNote}
          onKeyDown={handleEnter}
          onChange={(e) => setValueNote(e.target.value)}
        />
      </Box>
      <Controller
        name="taskCompleted"
        control={control}
        render={({ field }) => {
          const onSelected = (task) =>
            field.value.includes(task) ? field.value.filter((value) => value !== task) : [...field.value, task];

          const onTaskChange = (task) => {
            task.finish = task.finish === 0 ? 1 : 0;
            field.onChange(onSelected(task.id));
            doneNote(task); 
          };

          return (
            <>
              {notes !== null && notes.map((task) => (
                <TaskItem
                  key={task.id}
                  task={task}
                  checked={field.value.includes(task.id)}
                  onChange={() => { onTaskChange(task) }}
                  onDelete={() => handleDelete(task)}
                />
              ))}
            </>
          );
        }}
      />
    </Card>
  );
}

// ----------------------------------------------------------------------

TaskItem.propTypes = {
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  onDelete: PropTypes.func,
  task: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    label: PropTypes.string,
    finish: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  }),
};

function TaskItem({ task, checked, onChange, onDelete }) {
  const [open, setOpen] = useState(null);

  const handleOpenMenu = (event) => {
    setOpen(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setOpen(null);
  };

  const handleDelete = () => {
    handleCloseMenu();
    onDelete();
  };

  return (
    <Stack
      direction="row"
      sx={{
        px: 2,
        py: 0.75,
        ...(Boolean(task.finish) && {
          color: 'text.disabled',
          textDecoration: 'line-through',
        }),
      }}
    >
      <FormControlLabel
        control={<Checkbox checked={Boolean(task.finish)} onChange={onChange} />}
        label={task.note}
        sx={{ flexGrow: 1, m: 0 }}
      />

      <IconButton size="large" color="inherit" sx={{ opacity: 0.48 }} onClick={handleOpenMenu}>
        <Iconify icon={'eva:more-vertical-fill'} />
      </IconButton>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
            },
          },
        }}
      >
        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <Iconify icon={'eva:trash-2-outline'} sx={{ mr: 2 }} />
          Deletar
        </MenuItem>
      </Popover>
    </Stack>
  );
}
